import { apiRequest } from './api';

export interface PreCadastroAtendente {
  id: number;
  token: string;
  status: 'pendente' | 'aguardando_assinatura' | 'aguardando_aprovacao' | 'aprovado' | 'reprovado' | 'expirado';
  expires_at?: string | null;
  // Dados do contrato
  plano_id?: number | null;
  plano?: { id: number; nome: string; valor: number; velocidade_download: number; velocidade_upload: number } | null;
  valor_contratado?: number;
  acrescimo?: number;
  desconto?: number;
  modalidade?: 'pre-pago' | 'pos-pago';
  fidelidade?: number;
  pppoe_usuario?: string | null;
  pppoe_senha?: string | null;
  tipo_conexao?: string | null;
  nas_id?: number | null;
  nas?: { id: number; shortname?: string; nasname: string } | null;
  tipo_cliente?: string | null;
  tipo_pessoa?: 'pf' | 'pj';
  contribuinte_icms?: number | null;
  como_conheceu?: string | null;
  vendedor_id?: number | null;
  vendedor?: { id: number; nome: string } | null;
  indicado_por_id?: number | null;
  indicado_por?: { id: number; nome: string } | null;
  taxa_instalacao?: number;
  data_instalacao_agendada?: string | null;
  dia_vencimento?: number | null;
  titulo?: string | null;
  contrato_template?: number | null;
  cfop?: string | null;
  escritorio_id: number;
  escritorio?: { id: number; razao_social: string; nome_fantasia: string } | null;
  observacoes?: string | null;
  link_cliente?: string;
  nome?: string | null;
  nome_fantasia?: string | null;
  cpf_cnpj?: string | null;
  rg_ie?: string | null;
  data_nascimento?: string | null;
  nome_mae?: string | null;
  nome_pai?: string | null;
  email?: string | null;
  telefone?: string | null;
  cep?: string | null;
  endereco?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  referencia?: string | null;
  codigo_municipio?: string | null;
  documentos?: Record<string, string> | null;
  selfie_path?: string | null;
  assinatura?: string | null;
  assinado_em?: string | null;
  ip_assinatura?: string | null;
  lat_lon_assinatura?: string | null;
  user_agent?: string | null;
  // Controle
  aprovado_por?: number | null;
  aprovado_em?: string | null;
  motivo_reprovacao?: string | null;
  cliente_id_gerado?: number | null;
  contrato_id_gerado?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CriarPreCadastroPayload {
  plano_id?: number | null;
  valor_contratado?: number;
  acrescimo?: number;
  desconto?: number;
  modalidade?: 'pre-pago' | 'pos-pago';
  fidelidade?: number;
  pppoe_usuario?: string | null;
  pppoe_senha?: string | null;
  tipo_conexao?: string | null;
  nas_id?: number | null;
  tipo_cliente?: string | null;
  tipo_pessoa?: 'pf' | 'pj';
  contribuinte_icms?: number | null;
  como_conheceu?: string | null;
  vendedor_id?: number | null;
  indicado_por_id?: number | null;
  taxa_instalacao?: number;
  data_instalacao_agendada?: string | null;
  escritorio_id: number;
  observacoes?: string | null;
  // Dados opcionais do cliente preenchidos diretamente pelo atendente
  nome?: string | null;
  nome_fantasia?: string | null;
  cpf_cnpj?: string | null;
  rg_ie?: string | null;
  data_nascimento?: string | null;
  nome_mae?: string | null;
  nome_pai?: string | null;
  email?: string | null;
  telefone?: string | null;
  cep?: string | null;
  endereco?: string | null;
  numero?: string | null;
  complemento?: string | null;
  dia_vencimento?: number | null;
  titulo?: string | null;
  contrato_template?: number | null;
  cfop?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  referencia?: string | null;
  codigo_municipio?: string | null;
}

const preCadastroService = {
  criar: async (payload: CriarPreCadastroPayload): Promise<PreCadastroAtendente & { link_cliente: string }> => {
    const res = await apiRequest.post<PreCadastroAtendente & { link_cliente: string }>('/pre-cadastros', payload);
    if (!res.success || !res.data) throw new Error(res.message || 'Erro ao criar pré-cadastro');
    return res.data;
  },

  listar: async (params?: {
    status?: string; escritorio_id?: number; vendedor_id?: number; page?: number; limit?: number;
  }): Promise<{ data: PreCadastroAtendente[]; total: number; page: number; totalPages: number }> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.escritorio_id) qs.set('escritorio_id', String(params.escritorio_id));
    if (params?.vendedor_id) qs.set('vendedor_id', String(params.vendedor_id));
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));

    const res = await apiRequest.get<{ data: PreCadastroAtendente[]; total: number; page: number; totalPages: number }>(`/pre-cadastros?${qs}`);
    if (!res.success) throw new Error(res.message || 'Erro ao listar pré-cadastros');
    return res as any;
  },

  obter: async (id: number): Promise<PreCadastroAtendente> => {
    const res = await apiRequest.get<PreCadastroAtendente>(`/pre-cadastros/${id}`);
    if (!res.success || !res.data) throw new Error(res.message || 'Pré-cadastro não encontrado');
    return res.data;
  },

  atualizar: async (id: number, payload: Partial<PreCadastroAtendente>): Promise<PreCadastroAtendente> => {
    const res = await apiRequest.put<PreCadastroAtendente>(`/pre-cadastros/${id}`, payload);
    if (!res.success || !res.data) throw new Error(res.message || 'Erro ao atualizar pré-cadastro');
    return res.data;
  },

  aprovar: async (id: number): Promise<{ cliente_id: number; contrato_id: number }> => {
    const res = await apiRequest.post<{ cliente_id: number; contrato_id: number }>(`/pre-cadastros/${id}/aprovar`, {});
    if (!res.success || !res.data) throw new Error(res.message || 'Erro ao aprovar');
    return res.data;
  },

  reprovar: async (id: number, motivo?: string): Promise<void> => {
    const res = await apiRequest.post(`/pre-cadastros/${id}/reprovar`, { motivo });
    if (!res.success) throw new Error(res.message || 'Erro ao reprovar');
  },
};

export default preCadastroService;
